import {Box, Stack, Tooltip, Typography} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import HotelIcon from "@mui/icons-material/Hotel";
import CakeOutlined from "@mui/icons-material/CakeOutlined";
import {useTranslation} from "react-i18next";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {
    GuestCount,
    Reservation
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {
    AgeCategory
} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {
    getIconForLanguage,
    getNameForLanguage
} from "crqlar-react-common/dist/utils/translations.util";
import {formatDateRangePretty} from "crqlar-react-common/dist/utils/date.util";
import {AgeCategoriesElement} from "../../../../../common/uiElements/AgeCategoriesElement";
import {ALLERGY_ICON_MAP, getReservationDisplay} from "../../utils/reservation-utils";
import type {
    ReservationListAliasAgeCategoriesDTO,
} from "crqlar-react-common/dist/models/aggregation/reservationListAggregation.model";

export interface ReservationPreviewCardContentProps {
    reservation?: Reservation;
    profiles?: Profile[];
    accommodationResources?: Resource[];
    ageCategories?: AgeCategory[];
    guestCounts?: GuestCount[];
    aliasAgeCategories?: ReservationListAliasAgeCategoriesDTO;
    accommodationServiceName?: string;
    sourceTableName?: string;
    targetTableName?: string;
    // Pre-formatted accommodation stay range (e.g. "05.05.2026 – 12.05.2026").
    // When provided, it shows on its own line with a calendar icon.
    accommodationStayRange?: string;
}

const ReservationPreviewCardContent: React.FC<ReservationPreviewCardContentProps> = ({
                                                                                         reservation,
                                                                                         profiles = [],
                                                                                         accommodationResources = [],
                                                                                         ageCategories = [],
                                                                                         guestCounts,
                                                                                         aliasAgeCategories,
                                                                                         accommodationServiceName,
                                                                                         sourceTableName,
                                                                                         targetTableName,
                                                                                         accommodationStayRange,
                                                                                     }) => {
    const {t, i18n} = useTranslation();
    const {
        guestDisplayNames,
        guestCount,
        primaryLanguage,
        start,
        end,
        roomNames,
        timeRange,
        fromBookingEngine,
        birthdayNames,
        preferences,
    } = getReservationDisplay({
        reservation,
        profiles,
        ageCategories,
        accommodationResources,
        guestCounts,
    });

    const isSameDayReservation = !!start && !!end && start.toDateString() === end.toDateString();
    const isMultiDay = !!start && !!end && !isSameDayReservation;
    const locale = i18n.resolvedLanguage || i18n.language || "en";
    const stayRange = isMultiDay ? formatDateRangePretty(start, end, locale) : "";
    const displayedStayRange = accommodationStayRange || stayRange;
    const languageIcon = getIconForLanguage(primaryLanguage);
    const languageName = getNameForLanguage(primaryLanguage ?? undefined);
    if (!reservation) return null;

    return (
        <Box sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderLeft: "3px solid",
            borderLeftColor: "grey.600",
            borderRadius: 1,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            px: 1.5,
            py: 1.25,
        }}>
            <Stack spacing={0.25}>
                {guestDisplayNames.length > 0 ? guestDisplayNames.map((guestName, index) => (
                    <Typography
                        key={`${guestName}-${index}`}
                        variant="body2"
                        fontWeight={700}
                        sx={{fontSize: "0.9rem", lineHeight: 1.35}}
                    >
                        {guestName}
                    </Typography>
                )) : (
                    <Typography variant="body2" fontWeight={700}
                                sx={{fontSize: "0.9rem"}}>
                        —
                    </Typography>
                )}
            </Stack>

            <Box sx={{
                display: "flex",
                alignItems: "center",
                columnGap: 1.25,
                rowGap: 0.75,
                mt: 1,
                flexWrap: "wrap",
            }}>
                {languageIcon && (
                    <Box
                        component="img"
                        alt={languageName || primaryLanguage || ""}
                        title={languageName || primaryLanguage || ""}
                        src={languageIcon}
                        sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "0.5px solid rgba(0,0,0,0.15)",
                            flex: "0 0 auto",
                        }}
                    />
                )}
                {fromBookingEngine && (
                    <Tooltip title={t("Online Reservation")} arrow>
                        <BookOnlineIcon sx={{
                            fontSize: 15,
                            color: "text.secondary",
                            flex: "0 0 auto",
                            cursor: "default",
                        }}/>
                    </Tooltip>
                )}
                {roomNames && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                        <HotelIcon sx={{fontSize: 15, color: "text.secondary"}}/>
                        <Typography variant="body2" color="text.secondary">
                            {roomNames}
                        </Typography>
                    </Box>
                )}
                <AgeCategoriesElement
                    ageCategories={ageCategories}
                    ageCategoryCounts={guestCounts ?? reservation.guestCounts}
                    aliasAgeCategories={aliasAgeCategories}
                    totalGuests={guestCount}
                    variant="inline"
                />
                {sourceTableName && targetTableName && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                        <Typography variant="body2" color="text.secondary"
                                    fontWeight={600}>
                            {sourceTableName}
                        </Typography>
                        <ArrowForwardIcon
                            sx={{fontSize: 14, color: "text.secondary"}}/>
                        <Typography variant="body2" color="text.secondary"
                                    fontWeight={600}>
                            {targetTableName}
                        </Typography>
                    </Box>
                )}
                {birthdayNames.length > 0 && (
                    <Tooltip title={birthdayNames.map(name => t("It's {{name}}'s birthday", {name})).join(", ")} arrow>
                        <CakeOutlined sx={{fontSize: 15, color: "#9c27b0"}}/>
                    </Tooltip>
                )}
                {preferences.length > 0 && (
                    <Tooltip arrow title={
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {preferences.map((pref, i) => {
                                const mapping = ALLERGY_ICON_MAP[pref.preferenceType!];
                                if (!mapping) return null;
                                const Icon = mapping.icon;
                                return (
                                    <Stack key={`${pref.preferenceValue}-${i}`} direction="row" alignItems="center" gap={0.25}>
                                        <Icon sx={{fontSize: 12, color: mapping.color}}/>
                                        <Typography fontSize="0.7rem">{pref.preferenceValue}</Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    }>
                        <Stack direction="row" gap={0.25} sx={{cursor: "default"}}>
                            {Array.from(new Set(preferences.map(p => p.preferenceType))).map(type => {
                                const mapping = ALLERGY_ICON_MAP[type!];
                                if (!mapping) return null;
                                const Icon = mapping.icon;
                                return <Icon key={type} sx={{fontSize: 15, color: mapping.color}}/>;
                            })}
                        </Stack>
                    </Tooltip>
                )}
            </Box>

            {(isSameDayReservation || displayedStayRange || accommodationServiceName) && (
                <Stack spacing={0.625} sx={{
                    mt: 1,
                    pt: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                }}>
                    {isSameDayReservation && (
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                            <AccessTimeIcon sx={{fontSize: 15, color: "text.secondary"}}/>
                            <Typography variant="body2" color="text.secondary">
                                {timeRange}
                            </Typography>
                        </Box>
                    )}
                    {displayedStayRange && (
                        <Typography variant="body2" color="text.secondary">
                            <Box component="span" fontWeight={600}>{t("Stay")}:</Box>
                            {" "}{displayedStayRange}
                        </Typography>
                    )}
                    {accommodationServiceName && (
                        <Typography variant="body2" color="text.secondary">
                            <Box component="span" fontWeight={600}>{t("Rate")}:</Box>
                            {" "}{accommodationServiceName}
                        </Typography>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default ReservationPreviewCardContent;
